import {useCallback, useMemo} from 'react';
import {Connection} from 'reactflow';

import type {UnitOperationStatusType} from '../../procedure-runs/overview/procedureRunOverviewUtils';
import {
  canDrawOutgoingEdgeFromStatus,
  type FlowchartConnectionOptions,
  FlowchartConnectionMode,
  isConnectionAlignedWithProcedureEdge,
  isNewConnection,
  type ConnectionCapabilities,
  isUpstreamToActiveConnection,
} from './procedureRunDesignFlowchartConnectionUtils';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutEdge} from './useProcedureRunDesignFlowchartEdges';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartConnectionHandlersArgs {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  onDrawEdge: (spec: FlowchartEdgeSpec) => void;
  activeMethodId: string | null;
  isEditable: boolean;
  connectionOptions: FlowchartConnectionOptions;
}

export interface UseProcedureRunDesignFlowchartConnectionHandlersResult {
  nodesConnectable: boolean;
  isValidConnection: (connection: Connection) => boolean;
  onConnect: (connection: Connection) => void;
}

/**
 * Wires the drag-to-connect interaction for the flowchart: whether the canvas is connectable, how a
 * drag is validated, and what a completed drag does (reports the new edge up to the draft edge state).
 *
 * Validation is a composition of pure predicates from `procedureRunDesignFlowchartConnectionUtils`, so
 * future rules (e.g. node-status gating) can be added by ANDing in another predicate here.
 */
export function useProcedureRunDesignFlowchartConnectionHandlers({
  nodes,
  edges,
  onDrawEdge,
  activeMethodId,
  isEditable,
  connectionOptions,
}: UseProcedureRunDesignFlowchartConnectionHandlersArgs): UseProcedureRunDesignFlowchartConnectionHandlersResult {
  const nodesConnectable =
    isEditable &&
    (connectionOptions.connectionMode === FlowchartConnectionMode.PROCEDURE_RUN_PLAN ||
      (connectionOptions.connectionMode === FlowchartConnectionMode.PROCEDURE_RUN && activeMethodId != null));

  // Look up a grid node's connection capabilities by id so the validator stays in lockstep with the
  // derived capabilities on the nodes (read from node data, never parsed out of ids).
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const getConnectionCapabilities = useCallback(
    (nodeId: string): ConnectionCapabilities => {
      const node = nodesById.get(nodeId);
      return node != null && isProcedureRunDesignFlowchartNode(node)
        ? node.data.connectionCapabilities
        : {source: false, target: false};
    },
    [nodesById]
  );
  const getStatus = useCallback(
    (nodeId: string): UnitOperationStatusType | undefined => {
      const node = nodesById.get(nodeId);
      return node != null && isProcedureRunDesignFlowchartNode(node) ? node.data.status : undefined;
    },
    [nodesById]
  );
  const getMethodId = useCallback(
    (nodeId: string): string | null => {
      const node = nodesById.get(nodeId);
      return node != null && isProcedureRunDesignFlowchartNode(node) ? node.data.methodId : null;
    },
    [nodesById]
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      if (
        connectionOptions.connectionMode === FlowchartConnectionMode.DISABLED ||
        !isNewConnection(connection, edges)
      ) {
        return false;
      }
      if (connectionOptions.connectionMode === FlowchartConnectionMode.PROCEDURE_RUN_PLAN) {
        return isConnectionAlignedWithProcedureEdge(
          connection,
          getMethodId,
          connectionOptions.procedureMethodEdgeKeys
        );
      }
      return (
        canDrawOutgoingEdgeFromStatus(getStatus(connection.source)) &&
        isUpstreamToActiveConnection(connection, edges, getConnectionCapabilities)
      );
    },
    [connectionOptions, edges, getConnectionCapabilities, getMethodId, getStatus]
  );
  // Reports the new edge to the owner of the draft edge state; rendering and the live source-handle
  // recompute follow from the edge flowing back down.
  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source == null || connection.target == null) {
        return;
      }
      onDrawEdge({sourceNodeId: connection.source, targetNodeId: connection.target});
    },
    [onDrawEdge]
  );

  return {nodesConnectable, isValidConnection, onConnect};
}
